import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignmentsApi,
  auditApi,
  authApi,
  carsApi,
  contractsApi,
  dashboardApi,
  documentsApi,
  driversApi,
  expensesApi,
  notificationsApi,
  finesApi,
  fuelApi,
  type FuelFilters,
  gpsApi,
  reservationsApi,
  servicesApi,
  settingsApi,
  usersApi,
  vignettesApi,
  type AssignmentFilters,
  type AuditFilters,
  type CarFilters,
  type ContractFilters,
  type DocumentFilters,
  type DriverFilters,
  type FineFilters,
  type MonthFilters,
  type ReservationFilters,
  type RideFilters,
  type ServiceFilters,
  type VignetteFilters,
} from '../services/api';
import type {
  AssignmentCreate,
  AssignmentReturn,
  BrandingSettings,
  CarInput,
  ColumnMapping,
  ContractCreate,
  DocumentUpdate,
  DocumentUpload,
  DriverInput,
  FineInput,
  FuelInput,
  PageParams,
  ReservationInput,
  ServiceInput,
  TireInput,
  UserCreate,
  UserUpdate,
  VignetteInput,
} from '../types';

/**
 * Which cached queries depend on which entity. Used after mutations and when
 * a live update arrives from another user.
 */
export const ENTITY_DEPENDENCIES: Record<string, string[]> = {
  cars: ['cars', 'dashboard', 'assignments', 'services', 'documents', 'gps', 'contracts', 'vignettes', 'reservations', 'audit', 'expenses', 'fuel'],
  drivers: ['drivers', 'dashboard', 'assignments', 'gps', 'contracts', 'reservations', 'audit'],
  contracts: ['contracts', 'assignments', 'cars', 'reservations', 'dashboard', 'audit'],
  reservations: ['reservations', 'assignments', 'drivers', 'cars', 'dashboard', 'audit'],
  vignettes: ['vignettes', 'dashboard', 'audit', 'expenses'],
  fines: ['fines', 'dashboard', 'audit', 'expenses'],
  fuel: ['fuel', 'cars', 'dashboard', 'audit', 'expenses'],
  assignments: ['assignments', 'cars', 'contracts', 'reservations', 'dashboard', 'audit'],
  services: ['services', 'cars', 'dashboard', 'audit', 'expenses'],
  documents: ['documents', 'cars', 'dashboard', 'audit'],
  gps: ['gps', 'cars', 'drivers', 'dashboard', 'audit', 'expenses', 'fuel'],
  users: ['users', 'audit'],
  settings: ['settings', 'audit'],
};

function useInvalidate() {
  const queryClient = useQueryClient();
  return (entity: keyof typeof ENTITY_DEPENDENCIES) =>
    Promise.all(ENTITY_DEPENDENCIES[entity].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
}

// ---------------------------------------------------------------- auth / users
export const useChangePassword = () =>
  useMutation({ mutationFn: (v: { current: string; next: string }) => authApi.changePassword(v.current, v.next) });

export const useNotificationSettings = () => useQuery({ queryKey: ['notifications'], queryFn: notificationsApi.settings });

export const useSaveNotificationSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.save,
    onSuccess: (data) => queryClient.setQueryData(['notifications'], data),
  });
};

export const useTestEmail = () => useMutation({ mutationFn: notificationsApi.test });

export const useAuditDevices = (params?: AuditFilters, enabled = true) =>
  useQuery({ queryKey: ['audit', 'devices', params], queryFn: () => auditApi.devices(params), enabled });

export const useUsers = (params?: PageParams & { search?: string }) =>
  useQuery({ queryKey: ['users', params], queryFn: () => usersApi.list(params), placeholderData: keepPreviousData });

export const useCreateUser = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (data: UserCreate) => usersApi.create(data), onSuccess: () => invalidate('users') });
};

export const useUpdateUser = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserUpdate }) => usersApi.update(id, data),
    onSuccess: () => invalidate('users'),
  });
};

// ---------------------------------------------------------------- cars
export const useCars = (params?: CarFilters, enabled = true) =>
  useQuery({ queryKey: ['cars', 'list', params], queryFn: () => carsApi.list(params), placeholderData: keepPreviousData, enabled });

export const useCar = (id: number) =>
  useQuery({ queryKey: ['cars', 'detail', id], queryFn: () => carsApi.get(id), enabled: id > 0 });

export const useCarSummary = (id: number) =>
  useQuery({ queryKey: ['cars', 'summary', id], queryFn: () => carsApi.summary(id), enabled: id > 0 });

export const useCreateCar = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (data: CarInput) => carsApi.create(data), onSuccess: () => invalidate('cars') });
};

export const useUpdateCar = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CarInput & TireInput> }) => carsApi.update(id, data),
    onSuccess: () => invalidate('cars'),
  });
};

export const useRetireCar = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => carsApi.retire(id), onSuccess: () => invalidate('cars') });
};

// ---------------------------------------------------------------- drivers
export const useDrivers = (params?: DriverFilters, enabled = true) =>
  useQuery({
    queryKey: ['drivers', 'list', params],
    queryFn: () => driversApi.list(params),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useCreateDriver = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (data: DriverInput) => driversApi.create(data), onSuccess: () => invalidate('drivers') });
};

export const useUpdateDriver = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DriverInput> }) => driversApi.update(id, data),
    onSuccess: () => invalidate('drivers'),
  });
};

// ---------------------------------------------------------------- fuel
export const useFuelTransactions = (params?: FuelFilters) =>
  useQuery({ queryKey: ['fuel', 'list', params], queryFn: () => fuelApi.list(params), placeholderData: keepPreviousData });

export const useFuelSummary = (params: { date_from?: string; date_to?: string; car_id?: number }) =>
  useQuery({ queryKey: ['fuel', 'summary', params], queryFn: () => fuelApi.summary(params), placeholderData: keepPreviousData });

export const useSaveFuel = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<FuelInput> }) => (id ? fuelApi.update(id, data) : fuelApi.create(data as FuelInput)),
    onSuccess: () => invalidate('fuel'),
  });
};

export const useDeleteFuel = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => fuelApi.delete(id), onSuccess: () => invalidate('fuel') });
};

export const useFuelPreview = () => useMutation({ mutationFn: (files: File[]) => fuelApi.preview(files) });

export const useFuelImport = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ files, createMissing }: { files: File[]; createMissing: boolean }) => fuelApi.import(files, createMissing),
    onSuccess: () => invalidate('fuel'),
  });
};

// ---------------------------------------------------------------- fines
export const useFines = (params?: FineFilters) =>
  useQuery({ queryKey: ['fines', 'list', params], queryFn: () => finesApi.list(params), placeholderData: keepPreviousData });

export const useExpenseSummary = (year?: number) =>
  useQuery({ queryKey: ['expenses', 'summary', year], queryFn: () => expensesApi.summary(year), placeholderData: keepPreviousData });

export const useExpenseItems = (params: { year?: number; car_id?: number }, enabled = true) =>
  useQuery({ queryKey: ['expenses', 'items', params], queryFn: () => expensesApi.items(params), enabled, placeholderData: keepPreviousData });

export const useFineSummary = (year?: number) =>
  useQuery({ queryKey: ['fines', 'summary', year], queryFn: () => finesApi.summary(year), placeholderData: keepPreviousData });

export const useSaveFine = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<FineInput> }) => (id ? finesApi.update(id, data) : finesApi.create(data as FineInput)),
    onSuccess: () => invalidate('fines'),
  });
};

export const useDeleteFine = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => finesApi.delete(id), onSuccess: () => invalidate('fines') });
};

// ---------------------------------------------------------------- reservations
export const useReservations = (params?: ReservationFilters) =>
  useQuery({ queryKey: ['reservations', 'list', params], queryFn: () => reservationsApi.list(params), placeholderData: keepPreviousData });

export const useReservationCalendar = (dateFrom: string, dateTo: string) =>
  useQuery({
    queryKey: ['reservations', 'calendar', dateFrom, dateTo],
    queryFn: () => reservationsApi.calendar(dateFrom, dateTo),
    placeholderData: keepPreviousData,
  });

export const useSaveReservation = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: ReservationInput }) => (id ? reservationsApi.update(id, data) : reservationsApi.create(data)),
    onSuccess: () => invalidate('reservations'),
  });
};

export const useDeleteReservation = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => reservationsApi.delete(id), onSuccess: () => invalidate('reservations') });
};

export const useHandoverInternal = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => reservationsApi.handoverInternal(id), onSuccess: () => invalidate('reservations') });
};

export const useReservationDriver = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => reservationsApi.addDriver(id), onSuccess: () => invalidate('reservations') });
};

// ---------------------------------------------------------------- vignettes
export const useVignettes = (params?: VignetteFilters) =>
  useQuery({ queryKey: ['vignettes', 'list', params], queryFn: () => vignettesApi.list(params), placeholderData: keepPreviousData });

export const useVignetteOverview = () => useQuery({ queryKey: ['vignettes', 'overview'], queryFn: vignettesApi.overview });

export const useSaveVignette = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: VignetteInput }) => (id ? vignettesApi.update(id, data) : vignettesApi.create(data)),
    onSuccess: () => invalidate('vignettes'),
  });
};

export const useDeleteVignette = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => vignettesApi.delete(id), onSuccess: () => invalidate('vignettes') });
};

// ---------------------------------------------------------------- contracts
export const useContracts = (params?: ContractFilters) =>
  useQuery({ queryKey: ['contracts', params], queryFn: () => contractsApi.list(params), placeholderData: keepPreviousData });

export const useCreateContract = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (data: ContractCreate) => contractsApi.create(data), onSuccess: () => invalidate('contracts') });
};

export const useDeleteContract = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => contractsApi.delete(id), onSuccess: () => invalidate('contracts') });
};

// ---------------------------------------------------------------- assignments
export const useAssignments = (params?: AssignmentFilters) =>
  useQuery({
    queryKey: ['assignments', params],
    queryFn: () => assignmentsApi.list(params),
    placeholderData: keepPreviousData,
  });

export const useCreateAssignment = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: AssignmentCreate) => assignmentsApi.create(data),
    onSuccess: () => invalidate('assignments'),
  });
};

export const useReturnAssignment = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AssignmentReturn }) => assignmentsApi.returnCar(id, data),
    onSuccess: () => invalidate('assignments'),
  });
};

// ---------------------------------------------------------------- services
export const useServices = (params?: ServiceFilters) =>
  useQuery({ queryKey: ['services', 'list', params], queryFn: () => servicesApi.list(params), placeholderData: keepPreviousData });

export const useUpcomingServices = (days = 30) =>
  useQuery({ queryKey: ['services', 'upcoming', days], queryFn: () => servicesApi.upcoming(days) });

export const useMaintenanceOverview = () => useQuery({ queryKey: ['services', 'overview'], queryFn: servicesApi.overview });

export const useSaveService = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id?: number; data: ServiceInput }) =>
      id ? servicesApi.update(id, data) : servicesApi.create(data),
    onSuccess: () => invalidate('services'),
  });
};

export const useDeleteService = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => servicesApi.delete(id), onSuccess: () => invalidate('services') });
};

// ---------------------------------------------------------------- documents
export const useDocuments = (params?: DocumentFilters) =>
  useQuery({ queryKey: ['documents', params], queryFn: () => documentsApi.list(params), placeholderData: keepPreviousData });

export const useUploadDocument = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ file, data, onProgress }: { file: File; data: DocumentUpload; onProgress?: (p: number) => void }) =>
      documentsApi.upload(file, data, onProgress),
    onSuccess: () => invalidate('documents'),
  });
};

export const useUpdateDocument = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DocumentUpdate }) => documentsApi.update(id, data),
    onSuccess: () => invalidate('documents'),
  });
};

export const useDeleteDocument = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => documentsApi.delete(id), onSuccess: () => invalidate('documents') });
};

// ---------------------------------------------------------------- GPS
export const useGPSPreview = () =>
  useMutation({ mutationFn: ({ files, mapping }: { files: File[]; mapping?: ColumnMapping }) => gpsApi.preview(files, mapping) });

export const useGPSImport = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ files, mapping, createMissing }: { files: File[]; mapping?: ColumnMapping; createMissing: boolean }) =>
      gpsApi.import(files, mapping, createMissing),
    onSuccess: () => invalidate('gps'),
  });
};

export const useGPSMonths = (params?: MonthFilters) =>
  useQuery({ queryKey: ['gps', 'months', params], queryFn: () => gpsApi.months(params), placeholderData: keepPreviousData });

export const useGPSMonth = (id: number | undefined) =>
  useQuery({ queryKey: ['gps', 'month', id], queryFn: () => gpsApi.month(id!), enabled: !!id });

export const useDeleteGPSMonth = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => gpsApi.deleteMonth(id), onSuccess: () => invalidate('gps') });
};

export const useRides = (params?: RideFilters) =>
  useQuery({ queryKey: ['gps', 'rides', params], queryFn: () => gpsApi.rides(params), placeholderData: keepPreviousData });

export const useGPSSyncStatus = () => useQuery({ queryKey: ['gps', 'sync'], queryFn: gpsApi.syncStatus });

export const useGPSSync = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: gpsApi.sync, onSuccess: () => invalidate('gps') });
};

// ---------------------------------------------------------------- audit
export const useAuditLogs = (params?: AuditFilters) =>
  useQuery({ queryKey: ['audit', 'list', params], queryFn: () => auditApi.list(params), placeholderData: keepPreviousData });

export const useRecordHistory = (table: string, id: number, enabled = true) =>
  useQuery({ queryKey: ['audit', 'record', table, id], queryFn: () => auditApi.record(table, id), enabled: enabled && id > 0 });

export const useAuditTables = () => useQuery({ queryKey: ['audit', 'tables'], queryFn: auditApi.tables });
export const useAuditActions = () => useQuery({ queryKey: ['audit', 'actions'], queryFn: auditApi.actions });

// ---------------------------------------------------------------- settings / dashboard
export const usePublicSettings = () =>
  useQuery({ queryKey: ['settings', 'public'], queryFn: settingsApi.public, staleTime: 5 * 60_000 });

export const useUpdateSettings = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Partial<BrandingSettings>) => settingsApi.update(data),
    onSuccess: () => invalidate('settings'),
  });
};

export const useDashboardStats = () =>
  useQuery({ queryKey: ['dashboard', 'stats'], queryFn: dashboardApi.stats, refetchInterval: 5 * 60_000 });
